// eslint-disable-next-line no-unused-vars
import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { SkeletonText } from '@carbon/react';
import { bool, func, array } from 'prop-types';
import { ChevronUp, Euro, DollarSign } from 'lucide-react';

const StyledExchangeRatesSection = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 8px;
  align-items: center;
  color: #707070;

  .cds--skeleton__text {
    min-width: 200px;
    max-width: 200px;
  }

  .currency {
    display: flex;
    align-items: center;
    white-space: nowrap;
    margin-right: 5px;

    svg {
      margin-right: 8px;
    }
  }

  .mobileExchangeRates {
    display: none;
  }

  .desktopExchangeRate {
    display: flex;
    align-items: center;
    position: relative;
  }

  @media screen and (max-width: 767px) {
    justify-content: start;
    padding: 0 15px 15px;
    color: #3a3a3a;

    .mobileExchangeRates {
      display: flex;
      align-items: center;
      gap: 4px;
      padding-bottom: 8px;
    }

    .desktopExchangeRate {
      display: none;
    }
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 14px);
  right: 0;
  width: 100%;
  color: ${props => props.theme.colors.darkBlue};
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.products.colors.border};
  z-index: 9;
  box-shadow: 0 0 40px #00000029;
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? 'visible' : 'hidden'};
  transition: opacity 0.25s ease-out;

  &:after {
    display: block;
    position: absolute;
    top: -2px;
    right: 10px;
    width: 10px;
    height: 10px;
    background-color: #FFF;
    transform: rotate(45deg) translateX(-50%);
    content: '';
  }

  > div {
    padding: 10px 15px;
  }
`;

const StyledChevronUp = styled(ChevronUp)`
  color: inherit;
  transform: scale(0.9) ${props => props.$menuOpen ? '' : 'rotate(180deg)'};
  cursor: pointer;
`;

const ExchangeRatesSection = forwardRef(({
  menuOpen,
  onToggle,
  exchangeRates,
  exchangeRatesLoading,
  formatNumber,
  t,
}, ref) => {
  if (exchangeRatesLoading) {
    return (
      <div className="loading">
        <SkeletonText />
      </div>
    );
  }

  const euroRate = exchangeRates?.find(rate => rate.currency === 'EUR');

  return (
    <StyledExchangeRatesSection ref={ ref }>
      <div className="mobileExchangeRates">
        { exchangeRates?.map(element => (
          <div key={ element?.currency } className="currency">
            <span>{element?.currency} = </span>
            <span>
              {formatNumber(element?.rate, { currency: 'ron', maximumFractionDigits: 4 })}
            </span>
          </div>
        )) }
      </div>
      <div className="desktopExchangeRate">
        { t('common.exchange') }:
        <div key={ euroRate?.currency } className="currency">
          <span>{euroRate?.currency} = </span>
          <span>
            {formatNumber(euroRate?.rate, { currency: 'ron', maximumFractionDigits: 4 })}
          </span>
        </div>
        <StyledChevronUp
          onClick={ onToggle }
          $menuOpen={ menuOpen }
        />
        <DropdownMenu $open={ menuOpen }>
          {exchangeRates?.map(element => (
            <div key={ element?.currency } className="currency">
              { element?.currency === 'EUR' ? <Euro size={ 16 } /> : <DollarSign size={ 16 } /> }
              <span>{element?.currency} = </span>
              <span>
                {formatNumber(element?.rate, { currency: 'ron', maximumFractionDigits: 4 })}
              </span>
            </div>
          ))}
        </DropdownMenu>
      </div>
    </StyledExchangeRatesSection>
  );
});

ExchangeRatesSection.propTypes = {
  menuOpen: bool.isRequired,
  onToggle: func.isRequired,
  exchangeRates: array,
  exchangeRatesLoading: bool.isRequired,
  formatNumber: func.isRequired,
  t: func.isRequired,
};

export default ExchangeRatesSection;
